import { useState, type JSX } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { ArrowLeft, Copy, RefreshCw, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@caratuva/ui';
import type { WebhookDeliveryDTO } from '@caratuva/shared-types';
import {
  deleteWebhookSubscription,
  listWebhookDeliveries,
  listWebhookSubscriptions,
  rotateWebhookSecret,
} from '../lib/api';

export const Route = createFileRoute('/_app/developers/webhooks/$id')({
  component: WebhookDetail,
});

function WebhookDetail(): JSX.Element {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = Route.useParams();
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);

  const subscription = useQuery({
    queryKey: ['webhook-subscriptions'],
    queryFn: ({ signal }) => listWebhookSubscriptions(signal),
    select: (rows) => rows.find((r) => r.id === id) ?? null,
  });

  const deliveries = useQuery({
    queryKey: ['webhook-subscriptions', id, 'deliveries'],
    queryFn: ({ signal }) => listWebhookDeliveries(id, { limit: 50, signal }),
  });

  const rotate = useMutation({
    mutationFn: () => rotateWebhookSecret(id),
    onSuccess: (result) => {
      setRotatedSecret(result.secret);
      void queryClient.invalidateQueries({ queryKey: ['webhook-subscriptions'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteWebhookSubscription(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webhook-subscriptions'] });
      toast.success(
        intl.formatMessage({
          id: 'developers.webhooks.detail.deleted',
          defaultMessage: 'Webhook endpoint disabled.',
        }),
      );
      void navigate({ to: '/developers/webhooks' });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  function onRotate(): void {
    const ok = window.confirm(
      intl.formatMessage({
        id: 'developers.webhooks.detail.confirmRotate',
        defaultMessage:
          'Rotate the signing secret? The old secret stops working immediately and your endpoint must be updated.',
      }),
    );
    if (!ok) return;
    rotate.mutate();
  }

  function onDelete(): void {
    const ok = window.confirm(
      intl.formatMessage({
        id: 'developers.webhooks.detail.confirmDelete',
        defaultMessage:
          'Disable this endpoint? Pending deliveries will continue but no new events will be sent.',
      }),
    );
    if (!ok) return;
    remove.mutate();
  }

  return (
    <div className="space-y-4">
      <Link
        to="/developers/webhooks"
        className="inline-flex items-center gap-1 text-sm text-navy-600 hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        {intl.formatMessage({
          id: 'developers.webhooks.detail.back',
          defaultMessage: 'Back to webhooks',
        })}
      </Link>

      {subscription.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'developers.webhooks.detail.loading',
            defaultMessage: 'Loading endpoint...',
          })}
        </p>
      ) : !subscription.data ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'developers.webhooks.detail.notFound',
            defaultMessage: 'Webhook endpoint not found.',
          })}
        </p>
      ) : (
        <>
          <div className="rounded-md border border-navy-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-1">
                <h2 className="break-all font-mono text-sm text-navy-900">
                  {subscription.data.url}
                </h2>
                <div className="flex flex-wrap items-center gap-2 text-xs text-navy-600">
                  {subscription.data.active ? (
                    <Badge variant="outline">
                      {intl.formatMessage({
                        id: 'developers.webhooks.status.active',
                        defaultMessage: 'Active',
                      })}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      {intl.formatMessage({
                        id: 'developers.webhooks.status.disabled',
                        defaultMessage: 'Disabled',
                      })}
                    </Badge>
                  )}
                  <span>
                    {intl.formatMessage(
                      {
                        id: 'developers.webhooks.detail.events',
                        defaultMessage: '{count} events subscribed',
                      },
                      { count: subscription.data.eventTypes.length },
                    )}
                  </span>
                  <span>·</span>
                  <span className="tabular-nums">
                    {intl.formatMessage(
                      {
                        id: 'developers.webhooks.detail.created',
                        defaultMessage: 'Created {date}',
                      },
                      { date: new Date(subscription.data.createdAt).toLocaleDateString() },
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onRotate}
                  disabled={rotate.isPending || !subscription.data.active}
                >
                  <RefreshCw aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                  {intl.formatMessage({
                    id: 'developers.webhooks.detail.rotate',
                    defaultMessage: 'Rotate secret',
                  })}
                </Button>
                {subscription.data.active ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onDelete}
                    disabled={remove.isPending}
                  >
                    <Trash2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                    {intl.formatMessage({
                      id: 'developers.webhooks.detail.delete',
                      defaultMessage: 'Disable',
                    })}
                  </Button>
                ) : null}
              </div>
            </div>

            <details className="mt-4 group">
              <summary className="cursor-pointer text-xs text-navy-600 hover:text-navy-900">
                {intl.formatMessage({
                  id: 'developers.webhooks.detail.eventList',
                  defaultMessage: 'Subscribed events',
                })}
              </summary>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {subscription.data.eventTypes.map((event) => (
                  <code
                    key={event}
                    className="rounded bg-navy-50 px-2 py-1 font-mono text-xs text-navy-700"
                  >
                    {event}
                  </code>
                ))}
              </div>
            </details>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-navy-900">
              {intl.formatMessage({
                id: 'developers.webhooks.detail.deliveries',
                defaultMessage: 'Recent deliveries',
              })}
            </h3>
            {deliveries.isPending ? (
              <p className="text-sm text-navy-600">
                {intl.formatMessage({
                  id: 'developers.webhooks.detail.deliveriesLoading',
                  defaultMessage: 'Loading deliveries...',
                })}
              </p>
            ) : deliveries.isError ? (
              <p className="text-sm text-danger">
                {intl.formatMessage({
                  id: 'developers.webhooks.detail.deliveriesError',
                  defaultMessage: 'Failed to load deliveries.',
                })}
              </p>
            ) : deliveries.data.length === 0 ? (
              <p className="rounded-md border border-dashed border-navy-200 bg-white px-4 py-6 text-center text-sm text-navy-600">
                {intl.formatMessage({
                  id: 'developers.webhooks.detail.deliveriesEmpty',
                  defaultMessage: 'No deliveries yet. They will appear here as events fire.',
                })}
              </p>
            ) : (
              <div className="overflow-hidden rounded-md border border-navy-200 bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {intl.formatMessage({
                          id: 'developers.webhooks.detail.col.event',
                          defaultMessage: 'Event',
                        })}
                      </TableHead>
                      <TableHead>
                        {intl.formatMessage({
                          id: 'developers.webhooks.detail.col.status',
                          defaultMessage: 'Status',
                        })}
                      </TableHead>
                      <TableHead>
                        {intl.formatMessage({
                          id: 'developers.webhooks.detail.col.responseStatus',
                          defaultMessage: 'HTTP',
                        })}
                      </TableHead>
                      <TableHead>
                        {intl.formatMessage({
                          id: 'developers.webhooks.detail.col.attempt',
                          defaultMessage: 'Attempt',
                        })}
                      </TableHead>
                      <TableHead>
                        {intl.formatMessage({
                          id: 'developers.webhooks.detail.col.deliveredAt',
                          defaultMessage: 'Delivered',
                        })}
                      </TableHead>
                      <TableHead>
                        {intl.formatMessage({
                          id: 'developers.webhooks.detail.col.createdAt',
                          defaultMessage: 'Created',
                        })}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliveries.data.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell className="font-mono text-xs text-navy-700">
                          {d.eventType}
                        </TableCell>
                        <TableCell>
                          <DeliveryStatusBadge status={d.status} />
                        </TableCell>
                        <TableCell className="tabular-nums text-navy-600">
                          {d.responseStatus ?? '—'}
                        </TableCell>
                        <TableCell className="tabular-nums text-navy-600">{d.attempt}</TableCell>
                        <TableCell className="tabular-nums text-navy-600">
                          {d.deliveredAt ? new Date(d.deliveredAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell className="tabular-nums text-navy-600">
                          {new Date(d.createdAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      )}

      <RotatedSecretDialog secret={rotatedSecret} onClose={() => setRotatedSecret(null)} />
    </div>
  );
}

function DeliveryStatusBadge({ status }: { status: WebhookDeliveryDTO['status'] }): JSX.Element {
  const variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'warning' =
    status === 'delivered'
      ? 'default'
      : status === 'pending'
        ? 'secondary'
        : status === 'dead_letter'
          ? 'destructive'
          : 'warning';
  return <Badge variant={variant}>{status}</Badge>;
}

interface RotatedSecretDialogProps {
  secret: string | null;
  onClose: () => void;
}

function RotatedSecretDialog({ secret, onClose }: RotatedSecretDialogProps): JSX.Element {
  const intl = useIntl();

  function copy(): void {
    if (!secret) return;
    void navigator.clipboard.writeText(secret);
    toast.success(
      intl.formatMessage({
        id: 'developers.copied',
        defaultMessage: 'Copied to clipboard.',
      }),
    );
  }

  return (
    <Dialog open={secret !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({
              id: 'developers.webhooks.detail.rotated.title',
              defaultMessage: 'New signing secret',
            })}
          </DialogTitle>
          <DialogDescription>
            {intl.formatMessage({
              id: 'developers.webhooks.detail.rotated.description',
              defaultMessage:
                'The previous secret is now invalid. Update your endpoint with this new value.',
            })}
          </DialogDescription>
        </DialogHeader>
        {secret ? (
          <div className="flex items-center gap-2 rounded-md border border-navy-200 bg-navy-50 px-3 py-2">
            <code className="flex-1 break-all font-mono text-xs text-navy-900">{secret}</code>
            <button
              type="button"
              onClick={copy}
              className="rounded p-1.5 text-navy-600 hover:bg-white hover:text-navy-900"
              aria-label={intl.formatMessage({
                id: 'developers.copy',
                defaultMessage: 'Copy',
              })}
            >
              <Copy aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {intl.formatMessage({
              id: 'developers.reveal.done',
              defaultMessage: "I've saved it",
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
